/**
 * OrderedArray - Implements an array which maintains a permanent order
 */
'use strict';

import _ from 'lodash';

/**
 * OrderedArray is an array sorted by a comparator. Order is always maintained. Inserts are
 * performed by binary-search and then an insert at the correct location. Duplicates are allowed.
 *
 * @access internal
 */
class OrderedArray {


	/**
	 * Constructs an instance of an OrderedArray
	 *
	 * @param {Array} [contents] - the initial contents of the array (faster if sorted)
	 * @param {function} [iteratee] - a function that when given an element, returns the sorting parameter
	 */
	constructor( contents = [], iteratee = _.identity ) {
		this._values = [];
		this._iteratee = iteratee;

		if( _.isArray(contents)) {
			this.insertList(contents);
		}
		else {
			throw new Error('OrderedArray, initial contents must be an array');
		}
	}

	/**
     * number of values
	 */
	get length() {
		return this._values.length;
	}

	/**
	 * sorted shallow-copy of all values
	 */
	get values() {
		return this._values.slice();
	}

	/**
	 *  value at n
	 */
	at(n) {
		return this._values[n];
	}

	/**
	 *  extracts first value (minimum)
	 */
	shift() {
		return this._values.shift();
	}

	/**
	 *  extracts last value (maximum)
	 */
	pop() {
		return this._values.pop();
	}

	clear() {
		this.extract();
	}

	/**
	 * removes count values from start and returns them
	 */
	extract( start = 0, count = this.length - start ) {
		return this._values.splice(start, count);
	}

	/**
	 * scans count values from start, removes all which pass the filter, and returns them
	 */
	extractWithFilter( start, count, filter ) {
		if( !filter ) {
			throw( new Error('OrderedArray.extractWithFilter requires a filter'));
		}

		let extracted = [];

		// limit is the lesser of remaining or count
		let limit = this.length - start;
		if( count < limit ) {
			limit = count;
		}

		let inRun = false;
		let startOfRun;
		let runLength;

		for( let i = 0, next = start; i < limit; i++ ) {
			if( filter(this._values[next]) ) {
				// value should be extracted
				if( !inRun ) {
					// beginning a run
					startOfRun = next;
					runLength = 0;
					inRun = true;
				}
				// continuing a run
				runLength++;
			}
			else {
				// value should be skipped
				if( inRun ) {
					// switching out of a run, extract the values
					extracted = extracted.concat(this._values.splice(startOfRun, runLength));
					inRun = false;

					// need to backtrack on next since we just removed a bunch of values.
					next -= runLength;
				}
			}

			next++;
		}

		if( inRun ) {
			// ended during a run, extract remaining values
			extracted = extracted.concat(this._values.splice(startOfRun, runLength));
		}

		return extracted;
	}

	/**
	 * finds a range {start,end} given start/end values
	 */
	rangeByValue( startValue, endValue = startValue ) {
		let minValue;
		let maxValue;

		if( this._iteratee(startValue) <= this._iteratee(endValue) ) {
			minValue = startValue;
			maxValue = endValue;
		}
		else {
			minValue = endValue;
			maxValue = startValue;
		}

		let start = this.indexStart(minValue);
		let end = this.indexAfter(maxValue);

		return { start, end };
	}

	/**
	 * extract values by value. inclusive.
	 */
	extractByValue( startValue, endValue = startValue ) {
		let { start, end } = this.rangeByValue(startValue, endValue);

		return this.extract(start, end - start);
	}

	/**
	 * extracts all values between startValue and endValue which pass the filter. inclusive.
	 */
	extractByValueWithFilter( startValue, endValue, filter ) {
		let { start, end } = this.rangeByValue(startValue, endValue);

		return this.extractWithFilter(start, end - start, filter);
	}

	/**
 	 * returns a range of values
 	 */
	slice( start = 0, end = this.length ) {
		return this._values.slice(start, end);
	}

	/**
	 * returns values by value. inclusive
	 */
	sliceByValue( startValue, endValue = startValue ) {
		let { start, end } = this.rangeByValue(startValue, endValue);

		return this.slice(start, end);
	}

	/**
	 *  gets the index where the value starts (or would start)
	 */
	indexStart( value ) {
		return _.sortedIndexBy(this._values, value, this._iteratee);
	}

	/**
	 *  this is like getIndex instead, if the found value is NOT the same value,
	 *  then it returns -1 (like indexOf)
	 */
	indexOf( value ) {
		let pos = this.indexStart(value);

		if( this._iteratee(this._values[pos]) !== this._iteratee(value) ) {
			pos = -1;
		}

		return pos;
	}

	/**
	 * gets the first index which is AFTER the value specified.
	 */
	indexAfter( value ) {
		return _.sortedLastIndexBy(this._values, value, this._iteratee);
	}

	/**
	 *  values matching existing values are inserted after the existing values.
	 */
	insert( value ) {
		let pos = this.indexAfter(value);

		this._values.splice(pos, 0, value);
	}

	/**
	 * inserts a list of values and maintains order. values do not need to be sorted.
	 */
	insertList( values ) {
		if( !_.isArray(values) ) {
			throw new Error( `OrderedArray.insertMultiples requires an array as input : {${values}` );
		}

		// TODO: we could optimize this by assuming values are sparse and sorted. ie
		// if we inserted a value, then if the next value is >= and if the next value in the
		// array is greater than or equal, then insert, otherwise we have to fallback.
		// it may even be worth while to verify if the input values are sorted, and then we can determine
		// if they can be spliced in one go, or just appended, etc. Otherwise we have multiple
		// binary searches to perform
		values.forEach( value => this.insert(value) );
	}
}

export default OrderedArray;



// WEBPACK FOOTER //
// ./src/utils/OrderedArray.js